/**
 * Platform detection utilities
 * Detects the user's operating system and maps it to platform identifiers
 * used in the build system (darwin, win32, linux)
 */

export const Platform = {
  DARWIN: 'darwin',
  WIN32: 'win32',
  LINUX: 'linux',
} as const;

export type Platform = (typeof Platform)[keyof typeof Platform];

/**
 * Release-binary CPU architecture. Both Linux and Windows publish per-arch
 * binaries/installers (`kirin-linux-${arch}`, `Kirin-${version}-${arch}.exe`);
 * macOS ships a universal binary. Aligned with the artifacts published by
 * `.github/workflows/agent.yml`.
 */
export const Arch = {
  AMD64: 'amd64',
  ARM64: 'arm64',
} as const;

export type Arch = (typeof Arch)[keyof typeof Arch];

/**
 * Default architecture used when client-side detection is unavailable
 * (Safari/Firefox don't expose UACH, or an emulated browser misreports).
 * amd64 is by far the most common desktop architecture, and — critically on
 * Windows — the amd64 installer runs on Windows-on-ARM via x64 emulation,
 * whereas an arm64 build will not run on amd64. So amd64 is the safe default:
 * a "wrong" guess just costs an extra round-trip (or emulation), never a
 * broken install.
 */
export const DEFAULT_ARCH: Arch = Arch.AMD64;

interface UADataValues {
  architecture?: string;
  bitness?: string;
}

interface NavigatorUAData {
  getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

interface NavigatorWithUAData extends Navigator {
  userAgentData?: NavigatorUAData;
}

/**
 * Detects the visitor's CPU architecture via User-Agent Client Hints. OS-
 * agnostic (works for Linux and Windows visitors alike). Returns `null` when:
 *   - `navigator.userAgentData` is missing (Safari/Firefox at time of
 *     writing, headless / SSR contexts).
 *   - `getHighEntropyValues` rejects (permissions-policy denies UACH).
 *   - The reported architecture isn't one we publish a binary for.
 *
 * Callers should treat `null` as "fall back to DEFAULT_ARCH (amd64)" rather
 * than as an error. Note UACH reports the *browser process* architecture, so
 * an x64 browser emulated on Windows-on-ARM reports x86 — which is why amd64
 * is the safe fallback (it runs under emulation regardless).
 */
export async function detectArch(): Promise<Arch | null> {
  if (typeof window === 'undefined' || !window.navigator) {
    return null;
  }
  const ua = (window.navigator as NavigatorWithUAData).userAgentData;
  if (!ua || typeof ua.getHighEntropyValues !== 'function') {
    return null;
  }
  try {
    const hints = await ua.getHighEntropyValues(['architecture', 'bitness']);
    // UACH reports "arm" for both 32- and 64-bit ARM; bitness="64"
    // distinguishes arm64 from armv7. We only publish arm64, so we treat
    // 32-bit ARM as unknown (callers fall back to amd64).
    if (hints.architecture === 'arm' && hints.bitness === '64') {
      return Arch.ARM64;
    }
    if (hints.architecture === 'x86' && hints.bitness === '64') {
      return Arch.AMD64;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Detects the user's operating system platform
 * @returns Platform identifier (Platform.DARWIN, Platform.WIN32, Platform.LINUX) or null if detection fails
 */
export function detectPlatform(): Platform | null {
  if (typeof window === 'undefined' || !window.navigator) {
    return null;
  }

  const platform = window.navigator.platform?.toLowerCase() || '';
  const userAgent = window.navigator.userAgent?.toLowerCase() || '';

  // Check for macOS (darwin)
  // navigator.platform can be 'MacIntel', 'MacPPC', 'Mac68K', or 'darwin'
  if (
    platform.includes('mac') ||
    platform === Platform.DARWIN ||
    userAgent.includes('mac os x')
  ) {
    return Platform.DARWIN;
  }

  // Check for Windows (win32)
  // navigator.platform can be 'Win32', 'Win64', 'WinCE', or 'win32'
  if (
    platform.includes('win') ||
    platform === Platform.WIN32 ||
    userAgent.includes('windows')
  ) {
    return Platform.WIN32;
  }

  // Check for Linux
  // navigator.platform can be 'Linux', 'Linux x86_64', etc.
  if (
    platform.includes('linux') ||
    userAgent.includes('linux') ||
    (!platform.includes('win') &&
      !platform.includes('mac') &&
      userAgent.includes('x11'))
  ) {
    return Platform.LINUX;
  }

  // Detection failed
  return null;
}
